import { createFileRoute, Link, linkOptions, Outlet } from "@tanstack/react-router";

import { useSession } from "../../api/session";

export const Route = createFileRoute("/settings")({
	component: SettingsLayout,
});

const settingsLinks = linkOptions([
	{ to: "/settings", label: "Overview", activeOptions: { exact: true } },
	{ to: "/settings/identity", label: "Identity" },
	{ to: "/settings/profile", label: "Profile" },
	{ to: "/settings/team", label: "Team & Access" },
	{ to: "/settings/limits", label: "Limits" },
	{ to: "/settings/security", label: "Security" },
	{ to: "/settings/audit-log", label: "Audit log" },
]);

/**
 * The services the organization connects Ravnar to, and the keys it gives other software, each a page of its own. Only
 * owners and admins connect them or manage the keys.
 */
const integrationLinks = linkOptions([
	{ to: "/settings/api-keys", label: "API keys" },
	{ to: "/settings/revolut", label: "Revolut" },
]);

// 12px rows with a 6px radius; the router sets aria-current on the active link, so the active style hangs off it.
const settingsLinkClassName =
	"rounded-ctl px-[9px] py-1.5 text-ctl text-ink-2 transition-colors duration-[120ms] select-none hover:bg-surface hover:text-ink aria-[current=page]:bg-accent/10 aria-[current=page]:font-medium aria-[current=page]:text-ink aria-[current=page]:ring-1 aria-[current=page]:ring-accent/25 aria-[current=page]:ring-inset";

/**
 * Settings pages share a 188px navigation column; each page renders its own PageHeader to the right of it. The
 * integrations follow under a heading of their own, as the reference's engine room groups them; on a narrow screen the
 * links wrap into rows and the heading is left out.
 */
function SettingsLayout() {
	// Only owners and admins may read the audit log or the security rules, or connect an integration, so members get no
	// link to any of them.
	const { member } = useSession();
	const links =
		member.role === "member"
			? settingsLinks.filter(link => link.to !== "/settings/audit-log" && link.to !== "/settings/security")
			: settingsLinks;

	return (
		<div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[188px_minmax(0,1fr)]">
			<nav aria-label="Settings" className="flex flex-wrap gap-px lg:sticky lg:top-0 lg:flex-col">
				{links.map(({ label, ...options }) => (
					<Link key={options.to} {...options} className={settingsLinkClassName}>
						{label}
					</Link>
				))}
				{member.role === "member" ? null : (
					<>
						<p className="mt-3 mb-1 hidden px-[9px] text-label font-semibold tracking-caps text-ink-3 uppercase lg:block">
							Integrations
						</p>
						{integrationLinks.map(({ label, ...options }) => (
							<Link key={options.to} {...options} className={settingsLinkClassName}>
								{label}
							</Link>
						))}
					</>
				)}
			</nav>
			<div className="max-w-[880px] min-w-0">
				<Outlet />
			</div>
		</div>
	);
}
